import axios from '../axios-retried';
import { BASE_URL, env } from '../constants/env';
import { normalize } from 'normalizr';
import { eventsSchema, normalizeEvents, normalizeFiles } from '../reducers/schema';
import { EVENTS_ROUTE, CASES_ROUTE, EMPLOYERS_ROUTE } from '../constants/endpoints';
import operationEnum from '../constants/eventUpsertOperationEnum';
import * as sourceTypes from '../constants/calendarSourceTypes';
// import Utils from '../utils';

import moment from 'moment';

export function getCaseEventsAndFiles(employerId, selectedDate) {
  if (!selectedDate) selectedDate = new Date();
  const startDate = moment(selectedDate).startOf('month').subtract(7, 'days').toISOString();
  const endDate = moment(selectedDate).endOf('month').add(7, 'days').toISOString();
  const queryStr = `&startdate=${startDate}&enddate=${endDate}`;
  const url = employerId
    ? `getCaseEventsAndFilesByEmployerId/${employerId}`
    : `getCaseEventsAndFilesByUserId`;
  return axios.get(env(BASE_URL) + url + `?${queryStr}`).then((response) => {
    if (!response?.data?.data) return {};

    const eventsNormalized = normalizeEvents(response.data.data.calendar);
    const filesNormalized = normalizeFiles(response.data.data.files);

    return {
      entities: {
        calendar: eventsNormalized?.entities?.calendar || {},
        files: filesNormalized?.entities?.files || {},
      },
    };
  });
}

/**
 * Creates an event on the backend. Schema for the endpoints below.
   {
    id: "/calendar",
    type: "object",
    properties: {
      eventId: {type: 'integer'},
      caseId: {type:'integer'},
      eventName: {type: 'string', minLength: 1, maxLength: 255},
      startDate: {type: 'string'}, //ISO
      endDate: {type: 'string'}, //ISO
      allDay: {type: ['boolean', 'null']},
      location: {
        type: ['object', 'null'],
        properties: {
          locationName: {type: ["string", "null"]},
          address: {type: "string"},
          latitude: {type: ["string", "null"]},
          longitude: {type: ["string", "null"]},
          phone: {type: ["string", "null"]}
        },
        required: ["address"]
      },
      locationString: { type: ['string', 'null'] },
      phone: { type: ['string', 'null'] },
      extraData: { type: ['object', 'null'] },
      repeat: {
        type: ['object', 'null'],
        properties: {
          //https://developer.apple.com/documentation/eventkit/ekrecurrencerule/1507273-init
          frequency: {type: 'integer', minimum:0, maximum:3},
          interval: {type: 'integer', minimum:1},
          endDate: {type: ["string", "null"]} //ISO
        },
        required: ["frequency", "interval"]
      },
      reminder: {anyOf: [{type: 'number'}, {type: 'null'}]},
      notes: {type: ['string', 'null']},
      transportation: {type: ['integer', "null"]},
      timeZone: {type: ["string", "null"]},
      assignee: {type: ["integer", "null"]}, //UserId
    },
    required: ["eventName", "startDate", "endDate"]
  };
 * @param eventObj
 * @param sourceObjectId
 * @returns {Promise<T> | Promise.<TResult>}
 */
export function createEvent(eventObj, sourceObjectId) {
  let url = env(BASE_URL) + CASES_ROUTE + '/' + sourceObjectId + '/events';

  return axios.post(url, eventObj).then((response) => {
    eventObj.eventId = response.data.data.eventId;
    eventObj.updated = response.data.data.updated;
    eventObj.caseId = sourceObjectId;
    return eventObj;
  });
}

export function createEventsUnavailableBlocks(events, employerId) {
  const url = env(BASE_URL) + 'employers' + '/' + employerId + '/unavailable_blocks';

  return axios.post(url, { events }).then((response) => {
    return response.data.data;
  });
}

export function createEventMultiples(eventObj, employerId, caseData, patientExtraData) {
  const url = env(BASE_URL) + EMPLOYERS_ROUTE + '/' + employerId + '/cases/events/';
  const body = {
    event: eventObj,
    ...caseData,
    patientExtraData,
  };

  // return Promise.reject();

  return axios.post(url, body).then((response) => {
    return response.data.data;
  });
}

// reason is one of: 'disqualified' or 'cancelled'
export function cancelReadiness(caseId, employerId, reason = 'cancelled') {
  const url =
    env(BASE_URL) + EMPLOYERS_ROUTE + '/' + employerId + `/cases/${caseId}/cancelPatientReadiness/`;
  return axios.put(url, { reason }).then((response) => {
    return response.data.data;
  });
}

/**
 * Updates an event on the backend. See triggerCreateEventRequest for the eventObj details.
 * @param eventObj
 * @param sourceTypeId
 * @param sourceObjectId
 * @param operation
 * @param exceptionDate
 * @returns {Promise<T> | Promise.<TResult>}
 */
export function updateEvent(eventObj, sourceObjectId, operation, exceptionDate) {
  let endpoint;
  switch (operation) {
    case operationEnum.UPDATE_RECURRING_SINGLE_EVENT:
      endpoint =
        env(BASE_URL) +
        CASES_ROUTE +
        '/' +
        sourceObjectId +
        '/events/' +
        eventObj.eventId +
        '/recurring-update-single';
      eventObj.exceptionDate = exceptionDate;
      break;
    case operationEnum.UPDATE_RECURRING_FUTURE_EVENTS:
      endpoint =
        env(BASE_URL) +
        CASES_ROUTE +
        '/' +
        sourceObjectId +
        '/events/' +
        eventObj.eventId +
        '/recurring-update-future';
      eventObj.exceptionDate = exceptionDate;
      break;
    default:
      endpoint = env(BASE_URL) + CASES_ROUTE + '/' + sourceObjectId + '/events/' + eventObj.eventId;
      break;
  }
  console.log('updateEvent', eventObj, endpoint, sourceObjectId);
  return axios.put(endpoint, eventObj).then((response) => {
    eventObj.caseId = sourceObjectId;
    eventObj.updated = response.data.data.updated;
    return eventObj;
  });
}

export function getEventById(eventId, sourceObjectId) {
  return axios
    .get(env(BASE_URL) + CASES_ROUTE + '/' + sourceObjectId + '/events/' + eventId)
    .then((response) => {
      let eventObj = response.data.data;
      // await Utils.makeSlow(2000);
      return eventObj;
    });
}

export function deleteEvent(
  eventId,
  sourceObjectId,
  operation = operationEnum.DELETE_DEFAULT,
  exceptionDate = null
) {
  let eventObj = {};
  switch (operation) {
    case operationEnum.DELETE_RECURRING_SINGLE_EVENT:
      eventObj.exceptionDate = exceptionDate;
      return axios
        .put(
          env(BASE_URL) +
            CASES_ROUTE +
            '/' +
            sourceObjectId +
            '/events/' +
            eventId +
            '/recurring-delete-single',
          eventObj
        )
        .then((response) => {
          // Similar to update, we don't do anything here.
          // See getEventById for more details
          return eventId;
        });
      break;
    case operationEnum.DELETE_RECURRING_FUTURE_EVENTS:
      eventObj.exceptionDate = exceptionDate;
      return axios
        .put(
          env(BASE_URL) +
            CASES_ROUTE +
            '/' +
            sourceObjectId +
            '/events/' +
            eventId +
            '/recurring-delete-future',
          eventObj
        )
        .then((response) => {
          // Similar to update, we don't do anything here.
          // See getEventById for more details
          return eventId;
        });
      break;
    default:
      return axios
        .delete(env(BASE_URL) + CASES_ROUTE + '/' + sourceObjectId + '/events/' + eventId)
        .then((response) => {
          return eventId;
        });
  }
}

/**
 * Get list of did it values.
 * @param events Array of events, expects eventId to be int and startDate to be a javascript Date() object
 * @returns {Promise<T> | Promise.<TResult>} See here https://docs.google.com/document/d/1wX_dA1xcSRi1Tr3yi3DYzlBe5XNIJB2w3Z8Eua3L7m8/edit
 */
export function getDidItValues(events) {
  const pairs = events
    .map((e) => e.eventId + ';' + e.startDate.toISOString().slice(0, -5))
    .join('|');
  return axios.get(env(BASE_URL) + EVENTS_ROUTE + '/did-it/' + pairs).then((response) => {
    return response.data.data;
  });
}

/**
 * Creates/updates a did it value for a eventId, startDate pair
 * @param eventId int
 * @param startDate Date() object
 * @param didIt boolean
 * @param caseId
 * @returns {Promise<T> | Promise.<TResult>}
 */
export function updateDidItValue(eventId, startDate, didIt, caseId) {
  const pair = eventId + ';' + startDate.toISOString();
  const putObject = { didIt: didIt };
  return axios
    .put(
      env(BASE_URL) + CASES_ROUTE + '/' + caseId + '/' + EVENTS_ROUTE + '/did-it/' + pair,
      putObject
    )
    .then((response) => {
      return response.data.data;
    });
}

/**
 * Returns the did-it success ratio for the success panel. Response format:
 * {"status":"success"
 * ,"data":
 *  {"allHistory": {"total":{"events":"60","successratio":"0.07"},"byEventType":[{"eventype":"Other","events":"60","successratio":"0.07"}]}
 *    ,"thisWeek":{"total":{"events":"0","successratio":null},"byEventType":[]}
 *    ,"lastWeek":{"total":{"events":"0","successratio":null},"byEventType":[]}}
 * @param caseId
 * @returns {Promise<T> | Promise.<TResult>}
 */
export function getDidItSuccessRatio(caseId) {
  return axios
    .get(`${env(BASE_URL)}v2/${CASES_ROUTE}/${caseId}/${EVENTS_ROUTE}/did-it/success-ratio`)
    .then((response) => {
      return response.data.data;
    });
}
